import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  OutlinedInput,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  IconButton,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { VaultDocument } from '@/types/vault';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { useGetContact } from '@/api/endpoints/contact/useGetContact';
import { useGetContactDocuments } from '@/api/endpoints/contact/useGetContactDocuments';
import { useGetInboxConversations } from '@/api/endpoints/conversation/useGetInboxConversations';
import { useRenameDocument } from '@/api/endpoints/document/useRenameDocument';
import { useUpdateCategoryOfDocument } from '@/api/endpoints/document/useUpdateCategoryOfDocument';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { useGetDocumentCategories } from '@/api/endpoints/vault/useGetDocumentCategories';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { EditIcon } from '@/assets/icons/EditIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatFullDate } from '@/utils/date';
import { getResolvedRoutePath } from '@/utils/router';

// Import the conversations component
import DocumentViewModal from '@/components/document/DocumentViewModal';
import VaultDocumentTagModal from '@/components/vault/VaultDocumentTagModal';

import ContactConversationsTab from './ContactConversationsTab';

interface ContactViewPageParams {
  id: string;
}

interface ContactMetadata {
  additional_details?: string;
  [key: string]: unknown;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`contact-tabpanel-${index}`}
    aria-labelledby={`contact-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const TABLE_HEAD = [
  { id: 'name', label: 'Document Name', sortable: true, width: '30%' },
  { id: 'type', label: 'Document Category', sortable: false, width: '25%' },
  { id: 'tags', label: 'Tags', sortable: false, width: '30%' },
  { id: 'date', label: 'Date Added', sortable: true, width: '15%' },
];

// Helper function to format document name
const formatDocumentName = (name: string, maxLength = 50) => {
  if (name.length <= maxLength) return name;

  const lastDotIndex = name.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${name.slice(0, maxLength - 3)}...`;
  }

  const extension = name.slice(lastDotIndex);
  const nameWithoutExtension = name.slice(0, lastDotIndex);
  const maxNameLength = maxLength - extension.length - 3; // 3 for "..."

  return `${nameWithoutExtension.slice(0, maxNameLength)}...${extension}`;
};

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

interface DocumentNameEditorProps {
  initialValue: string;
  onSave: (newName: string) => void;
  onCancel: () => void;
}

const DocumentNameEditor: React.FC<DocumentNameEditorProps> = ({
  initialValue,
  onSave,
  onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.selectionStart = inputRef.current.value.length;
    }
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (inputRef.current && inputRef.current.value.trim()) {
        onSave(inputRef.current.value.trim());
      } else {
        onCancel();
      }
    } else if (event.key === 'Escape') {
      onCancel();
    }
    event.stopPropagation();
  };

  const handleBlur = () => {
    if (inputRef.current && inputRef.current.value.trim()) {
      onSave(inputRef.current.value.trim());
    } else {
      onCancel();
    }
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <input
      ref={inputRef}
      defaultValue={initialValue}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClick={handleClick}
      style={{
        width: '100%',
        height: '32px',
        border: '1px solid #1976d2',
        borderRadius: '4px',
        padding: '0 8px',
        fontSize: 'inherit',
        outline: 'none',
      }}
    />
  );
};

// Create ContactFilesTab component
interface ContactFilesTabProps {
  documents: VaultDocument[];
  onPreviewDocument: (documentId: string, categoryId?: number) => void;
  onRefetchDocuments: () => void;
}

const ContactFilesTab: React.FC<ContactFilesTabProps> = ({
  documents,
  onPreviewDocument,
  onRefetchDocuments,
}) => {
  const theme = useTheme();
  const [filterName, setFilterName] = useState('');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof VaultDocument>('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<VaultDocument | null>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  // API hooks for document management
  const { data: categories = [] } = useGetDocumentCategories();

  const updateCategoryOfDocumentMutation = useUpdateCategoryOfDocument({
    onSuccess: () => {
      onRefetchDocuments();
    },
  });

  const renameDocumentMutation = useRenameDocument({
    onSuccess: () => {
      onRefetchDocuments();
    },
  });

  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      onRefetchDocuments();
    },
  });

  const filteredDocs = useMemo(
    () =>
      documents.filter(doc => doc.original_name.toLowerCase().includes(filterName.toLowerCase())),
    [documents, filterName]
  );

  const sortedDocs = useMemo(
    () => [...filteredDocs].sort(getComparator(order, orderBy)),
    [filteredDocs, order, orderBy]
  );

  const displayedDocs = useMemo(
    () => sortedDocs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedDocs, page, rowsPerPage]
  );

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property: keyof VaultDocument) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: React.SyntheticEvent | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleCategoryChange = (id: string, newCategory: string) => {
    updateCategoryOfDocumentMutation.mutate({
      categoryId: Number(newCategory),
      id,
    });
  };

  const handleStartEditing = useCallback((document: VaultDocument) => {
    setEditingDocumentId(document.id);
  }, []);

  const handleSaveDocumentName = (newName: string) => {
    if (editingDocumentId && newName) {
      renameDocumentMutation.mutate({
        id: editingDocumentId,
        name: newName,
      });
      setEditingDocumentId(null);
    }
  };

  const handleCancelEditing = () => {
    setEditingDocumentId(null);
  };

  const handleDeleteClick = (documentId: string) => {
    setDocumentToDelete(documentId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate({ documentId: documentToDelete });
    }
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleTagDocument = (document: VaultDocument) => {
    setSelectedDocument(document);
    setIsTagModalOpen(true);
  };

  const handleTagModalClose = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
  };

  const handleTagModalSuccess = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
    onRefetchDocuments();
  };

  const handleDocumentNameClick = useCallback(
    (document: VaultDocument) => {
      if (clickTimerRef.current === null) {
        // First click
        clickTimerRef.current = setTimeout(() => {
          // Single click - open preview
          onPreviewDocument(
            document.id,
            document.category_id ? Number(document.category_id) : undefined
          );
          clickTimerRef.current = null;
        }, 200); // 200ms threshold for double click
      } else {
        // Second click - double click
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
        handleStartEditing(document);
      }
    },
    [onPreviewDocument, handleStartEditing]
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Documents ({documents.length})</Typography>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search documents..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.id === '' ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof VaultDocument)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {displayedDocs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {documents.length === 0
                        ? 'No documents found for this contact.'
                        : 'No documents match your search.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedDocs.map(row => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                      '&:hover .delete-icon': {
                        opacity: 1,
                      },
                    }}
                  >
                    <TableCell sx={{ width: '30%' }}>
                      {editingDocumentId === row.id ? (
                        <DocumentNameEditor
                          initialValue={row.original_name}
                          onSave={handleSaveDocumentName}
                          onCancel={handleCancelEditing}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                          onClick={() => handleDocumentNameClick(row)}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              '&:hover': {
                                color: 'primary.main',
                              },
                            }}
                            title={row.original_name}
                          >
                            {formatDocumentName(row.original_name)}
                          </Typography>
                          <IconButton
                            size="small"
                            className="delete-icon"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteClick(row.id);
                            }}
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              p: 0.5,
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.lighter',
                              },
                            }}
                          >
                            <Trash01Icon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: '25%' }}>
                      <Select
                        value={row.category_id?.toString() || ''}
                        onChange={e => handleCategoryChange(row.id, e.target.value)}
                        size="small"
                        sx={{
                          width: '100%',
                          height: 32,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.secondary[400],
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.secondary[600],
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.secondary[600],
                            borderWidth: '1px',
                          },
                          '& .MuiSelect-select': {
                            py: 1,
                            px: 1.5,
                            fontSize: '0.875rem',
                            lineHeight: 1.2,
                          },
                        }}
                      >
                        {categories.map(category => (
                          <MenuItem key={category.id} value={category.id.toString()}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ width: '30%' }}>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{
                          minHeight: 28,
                          py: 0.5,
                          '&:hover .edit-tags-icon': {
                            opacity: 1,
                          },
                        }}
                      >
                        {row.tags?.map(tag => {
                          let tagName = '';
                          if (tag.property_location_id && row.property_locations) {
                            const propertyLocation = row.property_locations.find(
                              l => String(l?.id) === String(tag.property_location_id)
                            );
                            tagName = propertyLocation?.name || '';
                          } else if (tag.building_id && row.buildings) {
                            const building = row.buildings.find(
                              b => String(b?.id) === String(tag.building_id)
                            );
                            tagName = building?.name || '';
                          } else if (tag.unit_id && row.units) {
                            const unit = row.units.find(u => String(u?.id) === String(tag.unit_id));
                            tagName = unit?.name || '';
                          } else if (tag.tenant_id && row.tenants) {
                            const tenant = row.tenants.find(
                              t => String(t?.id) === String(tag.tenant_id)
                            );
                            tagName = tenant?.full_name || '';
                          } else if (tag.property_manager_id && row.property_managers) {
                            const manager = row.property_managers.find(
                              m => String(m?.id) === String(tag.property_manager_id)
                            );
                            tagName = manager?.limitedUser?.full_name || '';
                          }

                          if (!tagName) return null;

                          return (
                            <Chip
                              key={`${row.id}-${tag.id}`}
                              label={tagName}
                              size="small"
                              sx={{
                                height: 28,
                                backgroundColor: 'transparent',
                                '& .MuiChip-label': {
                                  px: 1.5,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  lineHeight: 1.2,
                                },
                              }}
                            />
                          );
                        })}
                        <IconButton
                          size="small"
                          className="edit-tags-icon"
                          onClick={() => handleTagDocument(row)}
                          sx={{
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            p: 0.5,
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatFullDate(new Date(row.created_at))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredDocs.length
              )} of ${filteredDocs.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredDocs.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {/* Delete Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: { xs: '90%', sm: 500 },
          },
        }}
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag Modal */}
      {isTagModalOpen && selectedDocument && (
        <VaultDocumentTagModal
          document={selectedDocument}
          onClose={handleTagModalClose}
          onSuccess={handleTagModalSuccess}
        />
      )}
    </Stack>
  );
};

const ContactViewPage: React.FC = () => {
  const { id } = useParams<ContactViewPageParams>();
  const { selectedPropertyLocation } = usePropertyManager();

  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId, setPreviewDocumentCategoryId] = useState<number | null>(null);

  const contactId = parseInt(id, 10);
  const getContactQuery = useGetContact(contactId, !!selectedPropertyLocation);

  // Fetch inbox conversations to calculate unread count
  const { data: inboxConversations = [] } = useGetInboxConversations(
    {
      location_id: selectedPropertyLocation?.id,
    },
    !!selectedPropertyLocation
  );

  // Fetch documents for this contact using the new endpoint
  const { data: contactDocuments = [], refetch: refetchContactDocuments } = useGetContactDocuments(
    {
      contactId,
      location_id: selectedPropertyLocation?.id || 0,
    },
    !!selectedPropertyLocation && !!contactId
  );

  // Get contact email addresses for filtering conversations
  const contactEmails = useMemo(() => {
    if (!getContactQuery.data?.details) return [];
    return getContactQuery.data.details
      .filter(detail => detail.type === 'email')
      .map(detail => detail.value.toLowerCase());
  }, [getContactQuery.data?.details]);

  // Parse contact string to extract email
  const parseContact = (contactString: string) => {
    const emailRegex = /<([^>]+)>/;
    const emailMatch = contactString.match(emailRegex);

    if (emailMatch) {
      const email = emailMatch[1];
      return { email };
    }

    if (contactString.includes('@')) {
      return { email: contactString };
    }

    return { email: '' };
  };

  // Calculate unread count for this contact
  const unreadCount = useMemo(() => {
    if (!inboxConversations || contactEmails.length === 0) return 0;

    const contactConversations = inboxConversations.filter(conversation => {
      if (conversation.type !== 'received') return false;

      const { email } = parseContact(conversation.from);
      return contactEmails.some(contactEmail => email.toLowerCase().includes(contactEmail));
    });

    return contactConversations.filter(conv => conv.type === 'received' && !conv.isRead).length;
  }, [inboxConversations, contactEmails]);

  const handleBack = () => {
    history.push(PMPRoutes.contactList);
  };

  const handleEdit = () => {
    history.push(getResolvedRoutePath(PMPRoutes.contactEdit, { id }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePreviewDocument = (documentId: string, categoryId?: number) => {
    setPreviewDocumentId(documentId);
    setPreviewDocumentCategoryId(categoryId || null);
  };

  const handleClosePreview = () => {
    setPreviewDocumentId(null);
    setPreviewDocumentCategoryId(null);
  };

  if (!selectedPropertyLocation) {
    return (
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
        <Typography variant="body2" color="text.secondary">
          Please select a property location to view contacts.
        </Typography>
      </Stack>
    );
  }

  const renderProfileInformation = () => (
    <Stack spacing={3}>
      {/* Basic Information Card */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Basic Information
            </Typography>

            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Stack spacing={1} sx={{ flex: 1, alignItems: 'flex-start' }}>
                <Typography variant="h4">{getContactQuery.data?.name}</Typography>
                <Chip
                  label={getContactQuery.data?.role.replace(/_/g, ' ')}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Created: {new Date(getContactQuery.data?.created_at || '').toLocaleDateString()}
              </Typography>
            </Stack>

            {getContactQuery.data?.user_id && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  User ID
                </Typography>
                <Typography variant="body1">{getContactQuery.data.user_id}</Typography>
              </Stack>
            )}

            {(() => {
              const metadata = getContactQuery.data?.metadata as ContactMetadata | null;
              return (
                metadata?.additional_details && (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Additional Details
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {metadata.additional_details}
                    </Typography>
                  </Stack>
                )
              );
            })()}
          </Stack>
        </CardContent>
      </Card>

      {/* Contact Details Card */}
      {getContactQuery.data?.details && getContactQuery.data.details.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Contact Details
            </Typography>

            <Stack spacing={2}>
              {getContactQuery.data.details.map(detail => (
                <Card key={detail.id} variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {detail.type}
                          </Typography>
                          {detail.label && (
                            <Chip
                              label={detail.label}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          )}
                          {detail.is_primary && (
                            <Chip label="Primary" size="small" color="primary" variant="filled" />
                          )}
                        </Stack>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {detail.value}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Property Associations Card */}
      {getContactQuery.data?.associations && getContactQuery.data.associations.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Property Associations
            </Typography>

            <Stack spacing={3}>
              {/* Location Chips Row */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Locations
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {[
                    ...new Set(
                      getContactQuery.data.associations
                        .filter(association => association.location)
                        .map(association => association.location?.name)
                        .filter((name): name is string => Boolean(name))
                    ),
                  ].map(locationName => (
                    <Chip
                      key={locationName}
                      label={locationName}
                      color="secondary"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
      <DashboardHeader title="Contact Details">
        <Stack direction="row" spacing={1}>
          <Button
            color="primary"
            variant="contained"
            size="medium"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            disabled={getContactQuery.isLoading}
          >
            Edit Contact
          </Button>
          <Button
            color="secondary"
            variant="outlined"
            size="medium"
            startIcon={<ArrowLeftIcon />}
            onClick={handleBack}
          >
            Back to Contacts
          </Button>
        </Stack>
      </DashboardHeader>

      <Typography variant="body3" sx={{ mb: 2 }}>
        View contact information and details for {selectedPropertyLocation.name}.
      </Typography>

      {getContactQuery.isLoading && (
        <Typography variant="body2" color="text.secondary">
          Loading contact details...
        </Typography>
      )}

      {getContactQuery.isError && (
        <Typography variant="body2" color="error">
          Error loading contact: {getContactQuery.error?.message}
        </Typography>
      )}

      {getContactQuery.data && (
        <Box sx={{ flex: 1 }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="contact tabs">
              <Tab label="Profile Information" />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Conversations</span>
                    {unreadCount > 0 && (
                      <Chip
                        label={unreadCount}
                        size="small"
                        color="primary"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Files</span>
                    {contactDocuments.length > 0 && (
                      <Chip
                        label={contactDocuments.length}
                        size="small"
                        color="secondary"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            {renderProfileInformation()}
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <ContactConversationsTab contact={getContactQuery.data} />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <ContactFilesTab
              documents={contactDocuments}
              onPreviewDocument={handlePreviewDocument}
              onRefetchDocuments={refetchContactDocuments}
            />
          </TabPanel>
        </Box>
      )}

      {/* Document Preview Modal */}
      {previewDocumentId && (
        <DocumentViewModal
          id={previewDocumentId}
          categoryId={previewDocumentCategoryId ?? undefined}
          onClose={handleClosePreview}
        />
      )}
    </Stack>
  );
};

export default ContactViewPage;
